<template>
  <div class="wg-body">
    <template v-if="deletedScreen">
      <div class="wg-placeholder">
        <div class="pico"><i class="material-icons-outlined">delete</i></div>
        <div class="pt">Project deleted</div>
        <div class="ps">
          This project, its content plans, audits and history have been permanently deleted.
        </div>
        <div class="wg-placeholder-acts">
          <button class="wg-pill solid" @click="goToAllSites">Back to All Sites</button>
        </div>
      </div>
    </template>

    <template v-else-if="showGscWizard">
      <gsc-connect-wizard
        :site="site"
        :connection="connection"
        :freq-adverb="freqAdverb"
        :is-admin="whiteGloveMissionControlAdminUi"
        @back="showGscWizard = false"
        @completed="onWizardCompleted"
      />
    </template>

    <div v-else :key="loadKey">
      <!-- account already connected (e.g. via the campaigns Google button) but
           no site connection yet: show the setup form directly in place of the
           teaser card -->
      <gsc-connect-wizard
        v-if="showInlineGscWizard"
        embedded
        :site="site"
        :connection="connection"
        :freq-adverb="freqAdverb"
        :is-admin="whiteGloveMissionControlAdminUi"
        @completed="onWizardCompleted"
      />
      <gsc-connection-card
        v-else
        :connection="connection"
        :freq-adverb="freqAdverb"
        @open-wizard="showGscWizard = true"
        @updated="applyConnectionPayload"
      />

      <page-mapping-card
        v-if="hasActiveConnection"
        ref="pageMappingCard"
        :site="site"
        @counts-changed="onMappingCountsChanged"
        @approved="refreshPublishedUrls"
      />

      <published-urls-card
        ref="publishedUrlsCard"
        :site="site"
        :admin-can-add="whiteGloveMissionControlAdminUi"
        @counts-changed="onUrlCountsChanged"
        @mapping-changed="refreshMappingSuggestions"
      />

      <project-details-card
        :site="site"
        :campaign="domainLevelCampaign"
        @refresh-campaigns="loadCampaigns"
      />

      <danger-zone-card
        :site="site"
        @deleted="onProjectDeleted"
      />
    </div>

    <wg-toast ref="toast" />
  </div>
</template>

<script>
import { mixin } from '@/components/mixin'
import GscConnectionCard from '@/components/SitesSingleView/WhiteGlove/Settings/GscConnectionCard.vue'
import GscConnectWizard from '@/components/SitesSingleView/WhiteGlove/Settings/GscConnectWizard.vue'
import PageMappingCard from '@/components/SitesSingleView/WhiteGlove/Settings/PageMappingCard.vue'
import PublishedUrlsCard from '@/components/SitesSingleView/WhiteGlove/Settings/PublishedUrlsCard.vue'
import ProjectDetailsCard from '@/components/SitesSingleView/WhiteGlove/Settings/ProjectDetailsCard.vue'
import DangerZoneCard from '@/components/SitesSingleView/WhiteGlove/Settings/DangerZoneCard.vue'
import WgToast from '@/components/SitesSingleView/WhiteGlove/Settings/WgToast.vue'

export default {
  name: 'SettingsWhiteGlove',
  components: {
    GscConnectionCard,
    GscConnectWizard,
    PageMappingCard,
    PublishedUrlsCard,
    ProjectDetailsCard,
    DangerZoneCard,
    WgToast,
  },
  mixins: [mixin],
  provide () {
    return {
      /** Design-mock toast: wgToast(message, { label, onClick }?, 'success'|'error') */
      wgToast: (message, action = null, kind = 'success') => {
        if (this.$refs.toast) this.$refs.toast.show(message, action, kind)
      },
    }
  },
  data () {
    return {
      campaigns: [],
      deletedScreen: false,
      connection: null,
      availableGoogleAccount: false,
      pendingSuggestionsCount: 0,
      needsUrlCount: 0,
      showGscWizard: false,
      anchorTimer: null,
      freqAdverb: 'quarterly',
    }
  },
  computed: {
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    domainLevelCampaign () {
      const domainLevel = this.campaigns.filter((c) => c.isDomainLevelCampaign)
      return domainLevel.length > 0 ? domainLevel[0] : null
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    /**
     * Sub-account sessions (mission control) restore the selected relation
     * asynchronously after load; API calls made before that go out without
     * subAccountRelationId and resolve against the wrong owner. Re-run all
     * loads whenever the relation changes (same pattern as Layout.vue).
     */
    subAccountRelationId () {
      const r = this.$store.state.selectedSubAccountRelation
      return r && typeof r === 'object' && r.id != null ? r.id : null
    },
    loadKey () {
      return `${this.siteId}:${this.subAccountRelationId}`
    },
    hasActiveConnection () {
      return Boolean(this.connection) && this.connection.status !== 'disconnected'
    },
    /** No usable site connection, but a Google account is reusable: render the
     * setup form inline instead of the "Connect" teaser. */
    showInlineGscWizard () {
      const noUsableConnection = !this.connection || this.connection.status === 'disconnected'
      return noUsableConnection && this.availableGoogleAccount
    },
    whiteGloveMissionControlAdminUi () {
      return this.isMissionControlSession && this.$store.state.isWhiteGloveAdminMode
    },
  },
  watch: {
    '$route.query.anchor' () {
      this.scrollToAnchor()
    },
    loadKey: {
      handler () {
        this.deletedScreen = false
        this.showGscWizard = false
        this.connection = null
        this.loadCampaigns()
        this.loadConnection()
        this.loadReportFrequency()
      },
      immediate: true,
    },
  },
  mounted () {
    this.scrollToAnchor()
  },
  beforeDestroy () {
    clearTimeout(this.anchorTimer)
  },
  methods: {
    toast (message, action = null, kind = 'success') {
      if (this.$refs.toast) this.$refs.toast.show(message, action, kind)
    },
    loadCampaigns () {
      if (!this.siteId) {
        this.campaigns = []
        return Promise.resolve()
      }
      return this.$store.dispatch('getCampaignsForSite', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.campaigns = response.data.campaigns || []
          }
        })
        .catch(() => {})
    },
    loadConnection () {
      if (!this.siteId) return Promise.resolve()
      return this.$store.dispatch('getGscConnection', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.applyConnectionPayload(response.data)
          }
        })
        .catch(() => {
          this.connection = null
        })
    },
    applyConnectionPayload (payload) {
      this.connection = payload.connection || null
      this.pendingSuggestionsCount = payload.pendingSuggestionsCount || 0
      this.needsUrlCount = payload.needsUrlCount || 0
      if (!this.connection || this.connection.status === 'disconnected') {
        this.checkAvailableGoogleAccount()
      }
    },
    checkAvailableGoogleAccount () {
      this.$store.dispatch('getGoogleAccessToken')
        .then((response) => {
          const data = response && response.data
          this.availableGoogleAccount = Boolean(
            data && data.status === 'SUCCESS' && data.googleEmail)
        })
        .catch(() => {
          this.availableGoogleAccount = false
        })
    },
    onWizardCompleted (payload) {
      const wasConnected = Boolean(this.connection) && this.connection.status === 'connected'
      const wasExpired = Boolean(this.connection) && this.connection.status === 'expired'
      this.applyConnectionPayload(payload)
      this.showGscWizard = false

      let text = 'Search Console connected'
      if (wasExpired) {
        text = 'Search Console reconnected, tracking resumed'
      } else if (wasConnected) {
        text = 'Search Console account updated'
      } else if (this.connection && this.connection.monitoringEnabled) {
        text = 'Search Console connected, monitoring enabled'
      }
      this.toast(text)
    },
    /**
     * Cross-tab navigation contract: CTAs on the Wins tab push
     * { name: 'SettingsWhiteGlove', query: { anchor: 'wg-gsc' } }.
     * Scroll the anchored card into view and pulse-highlight it for ~2s.
     */
    scrollToAnchor () {
      const anchor = this.$route.query.anchor
      if (!anchor) return
      // Some cards (e.g. #wg-mapping) only render after the async connection
      // load, so poll for the target instead of a one-shot lookup.
      clearTimeout(this.anchorTimer)
      let attempts = 0
      const tryScroll = () => {
        const el = document.getElementById(anchor)
        if (el) {
          // collapsed-by-default cards open when they're the anchor target
          if (anchor === 'wg-mapping' && this.$refs.pageMappingCard) {
            this.$refs.pageMappingCard.expand()
          }
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          el.classList.add('wg-flash')
          setTimeout(() => el.classList.remove('wg-flash'), 2000)
        } else if (attempts < 20) {
          attempts += 1
          this.anchorTimer = setTimeout(tryScroll, 150)
          return
        }
        const query = { ...this.$route.query }
        delete query.anchor
        this.$router.replace({ query }).catch(() => {})
      }
      this.$nextTick(tryScroll)
    },
    onUrlCountsChanged ({ needsUrlCount }) {
      this.needsUrlCount = needsUrlCount
    },
    onMappingCountsChanged ({ pendingSuggestionsCount }) {
      this.pendingSuggestionsCount = pendingSuggestionsCount
    },
    loadReportFrequency () {
      // Frequency drives the "POP will check performance {monthly|quarterly}"
      // consent wording on the GSC card and wizard.
      if (!this.siteId) return
      this.$store.dispatch('getWinsReportSettings', this.siteId)
        .then((response) => {
          const settings = response.data && response.data.settings
          if (settings && settings.frequency) {
            this.freqAdverb = settings.frequency
          }
        })
        .catch(() => {})
    },
    refreshMappingSuggestions () {
      if (this.$refs.pageMappingCard) {
        this.$refs.pageMappingCard.loadSuggestions()
      }
    },
    refreshPublishedUrls () {
      if (this.$refs.publishedUrlsCard) {
        this.$refs.publishedUrlsCard.load()
      }
    },
    onProjectDeleted () {
      this.deletedScreen = true
    },
    goToAllSites () {
      this.$router.push('/sites')
    },
  },
}
</script>

<style lang="scss">
/* anchor-jump highlight - applied to card roots in child components, so
   this block is intentionally unscoped */
.wg-flash {
  animation: wgFlash 2s ease;
}
@keyframes wgFlash {
  0%, 50% {
    box-shadow: 0 0 0 2px #ff5700, 0 0 0 7px #ffeddd;
  }
  100% {
    box-shadow: 0 0 0 0 transparent;
  }
}
</style>

<style lang="scss" scoped>
@import './Settings/settings-card.scss';

/* tab body - Overview.vue .wg-overview--populated */
.wg-body {
  padding: 24px 0 32px;
}

.wg-placeholder-acts {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  justify-content: center;
}
</style>
